/**
 * Vehicle position store using Zustand with immer.
 *
 * Manages real-time vehicle positions outside React render cycle
 * for high-frequency updates without frame drops.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import type { DataSource, LineInfo, TransportMode } from '../types/api';
import { FRESHNESS, RENDER_CONFIG } from '../config';
import { lerp } from '../utils/interpolation';

// =============================================================================
// TYPES
// =============================================================================

export type VehicleVisualState = 'active' | 'stale' | 'dead';

export interface VehicleState {
  // Identity
  service_journey_id: string;
  journey_gid: string;

  // Current position (for rendering)
  latitude: number;
  longitude: number;

  // Target position (for interpolation)
  targetLatitude: number;
  targetLongitude: number;

  // Movement data
  speed_kmh: number | null;
  heading: number | null;
  at_stop: boolean;

  // Data source
  data_source: DataSource;

  // Line info
  line: LineInfo | null;

  // Journey direction (destination)
  direction: string | null;

  // Timing
  lastUpdateTime: number;  // Timestamp of last update
  time: string;            // ISO 8601 from server

  // Visual state
  visualState: VehicleVisualState;

  // Dual-source timing (for freshness indicators)
  v1LastUpdateTime: number | null;  // Last v1-da-gps update timestamp
  v4LastUpdateTime: number | null;  // Last v4-estimated update timestamp

  // V4 position (when dual-source available)
  // This is the estimated position from V4 polling when GPS is the primary source
  v4_position?: {
    latitude: number;
    longitude: number;
    data_source: DataSource;
    time: string;
  };

  // Distributed architecture metadata
  polled_at: string | null;  // When the poller queried the API
  poller_id: string | null;  // Which poller captured this position
}

export interface VehicleUpdate {
  service_journey_id: string;
  journey_gid: string;
  latitude: number;
  longitude: number;
  speed_kmh: number | null;
  heading: number | null;
  at_stop: boolean;
  data_source: DataSource;
  line: LineInfo | null;
  direction: string | null;
  time: string;
  // Distributed architecture metadata (optional)
  polled_at?: string | null;  // When the poller queried the API
  poller_id?: string | null;  // Which poller captured this position
}

export interface V4PositionUpdate {
  service_journey_id: string;
  latitude: number;
  longitude: number;
  data_source: DataSource;
  time: string;
}

export type SelectedMarkerType = 'vehicle' | 'v4-position' | 'v5-position';

interface VehicleStoreState {
  // Vehicles keyed by service_journey_id
  vehicles: Map<string, VehicleState>;

  // Selected vehicle (for info panel)
  selectedVehicleId: string | null;

  // Which marker was clicked (vehicle or v4-position)
  selectedMarkerType: SelectedMarkerType;

  // Actions
  setVehicle: (update: VehicleUpdate) => void;
  setV4Position: (update: V4PositionUpdate) => void;
  removeVehicle: (id: string) => void;
  setVisualState: (id: string, state: VehicleVisualState) => void;
  selectVehicle: (id: string | null, markerType?: SelectedMarkerType) => void;
  clearStaleVehicles: () => void;
  getVehiclesByMode: (modes: TransportMode[]) => VehicleState[];
  getVehicleCount: () => number;
}

// =============================================================================
// STORE
// =============================================================================

export const useVehicleStore = create<VehicleStoreState>()(
  immer((set, get) => ({
    vehicles: new Map(),
    selectedVehicleId: null,
    selectedMarkerType: 'vehicle' as SelectedMarkerType,

    setVehicle: (update) => {
      set((state) => {
        const existing = state.vehicles.get(update.service_journey_id);
        const now = Date.now();
        const isGps = update.data_source === 'v1-da-gps';

        if (existing) {
          // Compute current visual position (where vehicle appears to be right now)
          // This ensures smooth animation continuity when updates arrive mid-animation
          const elapsed = now - existing.lastUpdateTime;
          const t = Math.min(elapsed / RENDER_CONFIG.ANIMATION_DURATION_MS, 1.0);
          const currentVisualLat = lerp(existing.latitude, existing.targetLatitude, t);
          const currentVisualLon = lerp(existing.longitude, existing.targetLongitude, t);

          // Start new animation from current visual position to new target
          existing.latitude = currentVisualLat;
          existing.longitude = currentVisualLon;
          existing.targetLatitude = update.latitude;
          existing.targetLongitude = update.longitude;
          existing.speed_kmh = update.speed_kmh;
          existing.heading = update.heading;
          existing.at_stop = update.at_stop;
          existing.data_source = update.data_source;
          // Preserve existing line/direction when update has null values —
          // prevents grey flash when a source (e.g. V1-DA) lacks line metadata
          existing.line = update.line?.color_bg ? update.line : (update.line ?? existing.line);
          existing.direction = update.direction ?? existing.direction;
          existing.lastUpdateTime = now;
          existing.time = update.time;
          existing.visualState = 'active';
          // Update source-specific timestamp
          if (isGps) {
            existing.v1LastUpdateTime = now;
          } else {
            existing.v4LastUpdateTime = now;
          }
          // Update journey_gid if we have a better value
          if (update.journey_gid && (!existing.journey_gid || existing.journey_gid === 'unknown')) {
            existing.journey_gid = update.journey_gid;
          }
          // Update distributed architecture metadata
          existing.polled_at = update.polled_at ?? null;
          existing.poller_id = update.poller_id ?? null;
        } else {
          // Create new vehicle with source-specific timestamps
          state.vehicles.set(update.service_journey_id, {
            service_journey_id: update.service_journey_id,
            journey_gid: update.journey_gid,
            latitude: update.latitude,
            longitude: update.longitude,
            targetLatitude: update.latitude,
            targetLongitude: update.longitude,
            speed_kmh: update.speed_kmh,
            heading: update.heading,
            at_stop: update.at_stop,
            data_source: update.data_source,
            line: update.line,
            direction: update.direction,
            lastUpdateTime: now,
            time: update.time,
            visualState: 'active',
            v1LastUpdateTime: isGps ? now : null,
            v4LastUpdateTime: isGps ? null : now,
            polled_at: update.polled_at ?? null,
            poller_id: update.poller_id ?? null,
          });
        }
      });
    },

    setV4Position: (update) => {
      set((state) => {
        const vehicle = state.vehicles.get(update.service_journey_id);
        console.log('[VehicleStore] setV4Position called:', {
          service_journey_id: update.service_journey_id?.slice(-8),
          vehicleFound: !!vehicle,
          vehicleCurrentSource: vehicle?.data_source,
          v4PositionSource: update.data_source,
        });
        if (vehicle) {
          vehicle.v4_position = {
            latitude: update.latitude,
            longitude: update.longitude,
            data_source: update.data_source,
            time: update.time,
          };
          // Update v4 timestamp - v4_position is always v4-estimated data
          // This is critical for freshness tracking when GPS is primary
          vehicle.v4LastUpdateTime = Date.now();
          console.log('[VehicleStore] V4 position SET on vehicle:', update.service_journey_id?.slice(-8));
        }
      });
    },

    removeVehicle: (id) => {
      set((state) => {
        state.vehicles.delete(id);
        if (state.selectedVehicleId === id) {
          state.selectedVehicleId = null;
        }
      });
    },

    setVisualState: (id, visualState) => {
      set((state) => {
        const vehicle = state.vehicles.get(id);
        if (vehicle) {
          vehicle.visualState = visualState;
        }
      });
    },

    selectVehicle: (id, markerType = 'vehicle') => {
      set((state) => {
        state.selectedVehicleId = id;
        state.selectedMarkerType = markerType;
      });
    },

    clearStaleVehicles: () => {
      set((state) => {
        const now = Date.now();
        const toRemove: string[] = [];

        state.vehicles.forEach((vehicle, id) => {
          const age = now - vehicle.lastUpdateTime;

          // Remove vehicles that haven't received any update for CLEANUP_MS
          if (age >= FRESHNESS.CLEANUP_MS) {
            toRemove.push(id);
          }
        });

        toRemove.forEach((id) => {
          state.vehicles.delete(id);
          if (state.selectedVehicleId === id) {
            state.selectedVehicleId = null;
          }
        });
      });
    },

    getVehiclesByMode: (modes) => {
      const vehicles = get().vehicles;
      return Array.from(vehicles.values()).filter(
        (v) => v.line && modes.includes(v.line.transport_mode)
      );
    },

    getVehicleCount: () => {
      return get().vehicles.size;
    },
  }))
);

// =============================================================================
// SELECTORS (for optimized subscriptions)
// =============================================================================

export const selectVehicles = (state: VehicleStoreState) => state.vehicles;
export const selectSelectedVehicleId = (state: VehicleStoreState) => state.selectedVehicleId;

/**
 * Selector for the currently selected vehicle.
 * Note: Use with useShallow in consuming components to detect property changes.
 */
export const selectSelectedVehicle = (state: VehicleStoreState): VehicleState | null => {
  if (!state.selectedVehicleId) return null;
  return state.vehicles.get(state.selectedVehicleId) ?? null;
};
